import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
interface Viewer3DProps {
  onFocusChange: (category: string, isClick: boolean) => void;
  onNavigation?: () => void;
  selectedCategory?: string;
  selectedItem?: string;
}
export const Viewer3D = ({
  onFocusChange,
  onNavigation,
  selectedCategory,
  selectedItem
}: Viewer3DProps) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const frameIdRef = useRef<number | null>(null);
  const moveMarkerRef = useRef<THREE.Mesh | null>(null);
  const targetPositionRef = useRef<THREE.Vector3 | null>(null);
  const movingRef = useRef(false);

  // Navigation controls
  const isDraggingRef = useRef(false);
  const previousMousePosition = useRef({
    x: 0,
    y: 0
  });
  const mouseMovedSinceDown = useRef(false);
  const mouseRef = useRef({
    x: 0,
    y: 0
  });

  // Keyboard navigation state
  const keysPressed = useRef({
    w: false,
    a: false,
    s: false,
    d: false,
    arrowUp: false,
    arrowDown: false,
    arrowLeft: false,
    arrowRight: false
  });

  // Camera rotation state (separate from position)
  const cameraRotationRef = useRef({
    yaw: 0,
    pitch: 0
  });
  const targetRotationRef = useRef<{
    yaw: number;
    pitch: number;
  } | null>(null);

  // Pulse timing for highlight effect
  const pulseTimerRef = useRef<number>(0);
  const lastPulseTimeRef = useRef<number>(0);
  const isPulsingRef = useRef(false);
  const pulsePhaseRef = useRef(0); // 0-1 for smooth fade in/out

  // Store furniture objects and their positions
  const furnitureRef = useRef<{
    sofa: {
      group: THREE.Group | null;
      position: [number, number, number];
    };
    bed: {
      group: THREE.Group | null;
      position: [number, number, number];
    };
    kitchen: {
      group: THREE.Group | null;
      position: [number, number, number];
    };
  }>({
    sofa: {
      group: null,
      position: [-3.5, 0, -4.5]
    },
    bed: {
      group: null,
      position: [5.5, 0, 0]
    },
    kitchen: {
      group: null,
      position: [-6, 0, 3]
    }
  });

  // Preset camera positions for each category
  const cameraPresetsRef = useRef<{
    [key: string]: {
      position: THREE.Vector3;
      rotation: {
        yaw: number;
        pitch: number;
      };
    };
  }>({
    ukseliistud: {
      position: new THREE.Vector3(-0.5, 1.6, -2.5),
      // Near sofa
      rotation: {
        // Looking towards sofa with more right adjustment
        yaw: Math.PI / 2 - 0.3,
        // ~70 degrees left (more to the right)
        pitch: -0.1
      }
    },
    põrandaliistud: {
      position: new THREE.Vector3(3.5, 1.6, 2),
      // Near bed
      rotation: {
        yaw: -0.8,
        pitch: -0.1
      } // Looking slightly left to capture whole bed
    },
    valisnurgad: {
      position: new THREE.Vector3(-4, 1.6, 0.5),
      // Near kitchen
      rotation: {
        yaw: 2.3,
        pitch: -0.1
      } // Looking at kitchen
    }
  });

  // Store callbacks in refs to avoid re-initialization
  const onFocusChangeRef = useRef(onFocusChange);
  const onNavigationRef = useRef(onNavigation);
  onFocusChangeRef.current = onFocusChange;
  onNavigationRef.current = onNavigation;

  // Store selectedCategory in ref to access in animate function
  const selectedCategoryRef = useRef(selectedCategory);
  selectedCategoryRef.current = selectedCategory;

  // Store references to scene functions
  const sceneFunctionsRef = useRef<{
    createSofa?: Function;
    createBed?: Function;
    createKitchenFurniture?: Function;
  }>({});

  // Store interactive objects globally
  const interactiveObjectsRef = useRef<THREE.Group[]>([]);
  useEffect(() => {
    if (!mountRef.current) return;
    interactiveObjectsRef.current = []; // Reset on init
    let lastFocusedObject: THREE.Group | null = null;
    let lastHoveredCategory: string = '';
    const raycaster = new THREE.Raycaster();
    const init = () => {
      // Scene
      sceneRef.current = new THREE.Scene();
      sceneRef.current.background = new THREE.Color(0xf5f5f5);

      // === NEW realistic textures ===
      const textureLoader = new THREE.TextureLoader();
      textureLoader.setCrossOrigin('anonymous');
      const woodTexture = textureLoader.load('https://threejsfundamentals.org/threejs/resources/images/wood.jpg');
      woodTexture.wrapS = woodTexture.wrapT = THREE.RepeatWrapping;
      woodTexture.repeat.set(6, 4);
      // === END textures ===

      // Camera
      cameraRef.current = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
      cameraRef.current.position.set(0, 1.6, 0); // Start in the center of the room

      // Initialize camera rotation with safe starting values
      cameraRotationRef.current = {
        yaw: 0,
        pitch: 0
      };
      updateCameraRotation();

      // Renderer
      rendererRef.current = new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: 'high-performance'
      });
      rendererRef.current.setSize(window.innerWidth, window.innerHeight);
      rendererRef.current.shadowMap.enabled = true;
      rendererRef.current.shadowMap.type = THREE.PCFSoftShadowMap;
      mountRef.current?.appendChild(rendererRef.current.domElement);

      // Lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      sceneRef.current.add(ambientLight);
      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(5, 10, 7);
      directionalLight.castShadow = true;
      directionalLight.shadow.bias = -0.0005;
      sceneRef.current.add(directionalLight);

      // Room and Floor
      const roomWidth = 14;
      const roomDepth = 10;
      const roomGeometry = new THREE.BoxGeometry(roomWidth, 4, roomDepth);
      const roomMaterial = new THREE.MeshStandardMaterial({
        color: 0xeeeeee,
        // A light gray, modern color
        side: THREE.BackSide,
        roughness: 0.8
      });
      const room = new THREE.Mesh(roomGeometry, roomMaterial);
      room.position.y = 2;
      room.userData.isRoom = true;
      sceneRef.current.add(room);
      const floorGeometry = new THREE.PlaneGeometry(roomWidth, roomDepth);
      const floorMaterial = new THREE.MeshStandardMaterial({
        map: woodTexture,
        roughness: 0.6,
        metalness: 0.1
      });
      const floor = new THREE.Mesh(floorGeometry, floorMaterial);
      floor.rotation.x = -Math.PI / 2;
      floor.position.y = 0.001;
      floor.receiveShadow = true;
      floor.userData.isFloor = true;
      sceneRef.current.add(floor);

      // Simple windows (semi-transparent planes)
      const windowMaterial = new THREE.MeshStandardMaterial({
        color: 0x87ceeb,
        transparent: true,
        opacity: 0.35,
        side: THREE.DoubleSide
      });
      const windowGeometry = new THREE.PlaneGeometry(2, 1.5);
      // Left wall window
      const windowLeft = new THREE.Mesh(windowGeometry, windowMaterial);
      windowLeft.position.set(-roomWidth / 2 + 0.05, 2, -1);
      windowLeft.rotation.y = Math.PI / 2;
      sceneRef.current.add(windowLeft);
      // Right wall window
      const windowRight = windowLeft.clone();
      windowRight.position.set(roomWidth / 2 - 0.05, 2, 1);
      windowRight.rotation.y = -Math.PI / 2;
      sceneRef.current.add(windowRight);

      // === Additional realistic lighting ===
      const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.4);
      hemiLight.position.set(0, 10, 0);
      sceneRef.current.add(hemiLight);
      const pointLightLiving = new THREE.PointLight(0xffffff, 0.6, 20);
      pointLightLiving.position.set(-roomWidth / 2 + 3, 3.5, 0);
      pointLightLiving.castShadow = true;
      pointLightLiving.shadow.bias = -0.0005;
      sceneRef.current.add(pointLightLiving);
      const pointLightBed = new THREE.PointLight(0xffffff, 0.6, 20);
      pointLightBed.position.set(roomWidth / 2 - 3, 3.5, 0);
      pointLightBed.castShadow = true;
      pointLightBed.shadow.bias = -0.0005;
      sceneRef.current.add(pointLightBed);

      // Helper functions to create furniture (re-added after refactor)
      const createSofa = (pos: [number, number, number], category: string, color: number, itemId: string = 'item1') => {
        const sofaGroup = new THREE.Group();

        // Determine sofa style based on itemId
        let sofaWidth = 2.5;
        let sofaDepth = 1;
        let sofaColor = color;
        let isCorner = false;
        let hasArmRests = true;
        if (itemId === 'item1') {
          // Milano 3-kohaline - standard 3-seater
          sofaWidth = 2.5;
          sofaColor = 0x4169E1; // Royal Blue
        } else if (itemId === 'item2') {
          // Oslo nurgadiivan - corner sofa
          sofaWidth = 3.2;
          sofaDepth = 1.2;
          isCorner = true;
          sofaColor = 0x228B22; // Forest Green
        } else if (itemId === 'item3') {
          // Stockholm tugitool - armchair
          sofaWidth = 1.2;
          sofaDepth = 0.9;
          sofaColor = 0xDC143C; // Crimson
        }

        // Main sofa material
        const baseMaterial = new THREE.MeshStandardMaterial({
          color: sofaColor,
          roughness: 0.8,
          // A bit more rough for fabric
          metalness: 0.1
        });

        // Main sofa base
        const baseGeometry = new THREE.BoxGeometry(sofaWidth, 0.4, sofaDepth);
        const base = new THREE.Mesh(baseGeometry, baseMaterial);
        base.position.set(0, 0.2, 0);
        base.castShadow = true;
        base.receiveShadow = true;
        sofaGroup.add(base);

        // Sofa back
        const backGeometry = new THREE.BoxGeometry(sofaWidth, 0.5, 0.2);
        const back = new THREE.Mesh(backGeometry, baseMaterial);
        back.position.set(0, 0.65, -sofaDepth / 2 + 0.1);
        back.castShadow = true;
        back.receiveShadow = true;
        sofaGroup.add(back);

        // Armrests
        if (hasArmRests) {
          const armGeometry = new THREE.BoxGeometry(0.2, 0.3, sofaDepth);
          const leftArm = new THREE.Mesh(armGeometry, baseMaterial);
          leftArm.position.set(-sofaWidth / 2 + 0.1, 0.55, 0);
          leftArm.castShadow = true;
          leftArm.receiveShadow = true;
          sofaGroup.add(leftArm);
          const rightArm = leftArm.clone();
          rightArm.position.set(sofaWidth / 2 - 0.1, 0.55, 0);
          sofaGroup.add(rightArm);
        }

        // For corner sofa, we removed the L-shaped extension

        // Seat Cushions
        const cushionMaterial = new THREE.MeshStandardMaterial({
          color: sofaColor,
          roughness: 0.9,
          metalness: 0.05
        });
        if (itemId === 'item3') {
          // Single cushion for armchair
          const cushionGeometry = new THREE.BoxGeometry(sofaWidth * 0.8, 0.15, sofaDepth * 0.8);
          const seatCushion = new THREE.Mesh(cushionGeometry, cushionMaterial);
          seatCushion.position.set(0, 0.475, 0.05);
          sofaGroup.add(seatCushion);

          // Single back cushion
          const backCushionGeometry = new THREE.BoxGeometry(sofaWidth * 0.8, 0.4, 0.15);
          const backCushion = new THREE.Mesh(backCushionGeometry, cushionMaterial);
          backCushion.position.set(0, 0.7, -sofaDepth / 2 + 0.18);
          backCushion.rotation.x = -Math.PI / 12;
          sofaGroup.add(backCushion);
        } else {
          // Multiple cushions for sofas
          const numCushions = itemId === 'item1' ? 3 : 2;
          const cushionWidth = (sofaWidth - 0.4) / numCushions;
          const cushionGeometry = new THREE.BoxGeometry(cushionWidth - 0.1, 0.15, sofaDepth * 0.8);
          for (let i = 0; i < numCushions; i++) {
            const xPos = -sofaWidth / 2 + 0.2 + cushionWidth / 2 + i * cushionWidth;
            const seatCushion = new THREE.Mesh(cushionGeometry, cushionMaterial);
            seatCushion.position.set(xPos, 0.475, 0.05);
            sofaGroup.add(seatCushion);

            // Back cushions
            const backCushionGeometry = new THREE.BoxGeometry(cushionWidth - 0.1, 0.4, 0.15);
            const backCushion = new THREE.Mesh(backCushionGeometry, cushionMaterial);
            backCushion.position.set(xPos, 0.7, -sofaDepth / 2 + 0.18);
            backCushion.rotation.x = -Math.PI / 12;
            sofaGroup.add(backCushion);
          }
        }
        sofaGroup.position.set(...pos);
        sofaGroup.userData = {
          category,
          originalScale: sofaGroup.scale.clone(),
          originalColor: sofaColor,
          itemId: itemId
        };
        interactiveObjectsRef.current.push(sofaGroup as any);
        sceneRef.current?.add(sofaGroup);

        // Store reference for updating
        if (category === 'ukseliistud') {
          furnitureRef.current.sofa.group = sofaGroup;
        }
      };
      const createBed = (pos: [number, number, number], category: string, color: number, itemId: string = 'item1') => {
        const bedGroup = new THREE.Group();

        // Determine bed style based on itemId
        let bedWidth = 2;
        let bedLength = 3;
        let bedColor = color;
        let frameColor = 0x8B4513;
        let hasHeadboard = true;
        let hasFootboard = false;
        if (itemId === 'item1') {
          // Premium voodi 160x200
          bedWidth = 1.6;
          bedLength = 2.0;
          bedColor = 0xFF69B4; // Hot Pink bedding
          frameColor = 0x654321; // Dark Brown
        } else if (itemId === 'item2') {
          // Deluxe voodi 180x200
          bedWidth = 1.8;
          bedLength = 2.0;
          bedColor = 0x800080; // Purple bedding
          frameColor = 0x000000; // Black frame
          hasFootboard = true;
        }

        // --- Materials ---
        const frameMaterial = new THREE.MeshStandardMaterial({
          color: frameColor,
          roughness: 0.8,
          metalness: 0.1
        });
        const mattressMaterial = new THREE.MeshStandardMaterial({
          color: 0xffffff,
          // White mattress
          roughness: 0.95,
          metalness: 0.0
        });
        const beddingMaterial = new THREE.MeshStandardMaterial({
          color: bedColor,
          roughness: 0.9,
          metalness: 0.0
        });

        // --- Bed components ---
        // Bed frame base
        const frameGeometry = new THREE.BoxGeometry(bedWidth + 0.2, 0.3, bedLength + 0.2);
        const frame = new THREE.Mesh(frameGeometry, frameMaterial);
        frame.position.set(0, 0.15, 0);
        frame.castShadow = true;
        frame.receiveShadow = true;
        bedGroup.add(frame);

        // Mattress
        const mattressGeometry = new THREE.BoxGeometry(bedWidth, 0.25, bedLength);
        const mattress = new THREE.Mesh(mattressGeometry, mattressMaterial);
        mattress.position.set(0, 0.425, 0);
        mattress.castShadow = true;
        mattress.receiveShadow = true;
        bedGroup.add(mattress);

        // Duvet
        const duvetGeometry = new THREE.BoxGeometry(bedWidth, 0.25, bedLength * 0.8, 4, 4, 4);
        const duvet = new THREE.Mesh(duvetGeometry, beddingMaterial);
        const mattressTopY = mattress.position.y + mattress.geometry.parameters.height / 2;
        duvet.position.set(0, mattressTopY + duvet.geometry.parameters.height / 2 - 0.02, bedLength * 0.1);
        duvet.castShadow = true;
        duvet.receiveShadow = true;
        bedGroup.add(duvet);

        // Headboard
        if (hasHeadboard) {
          const headHeight = itemId === 'item2' ? 1.5 : 1; // Deluxe has taller headboard
          const headGeometry = new THREE.BoxGeometry(bedWidth + 0.2, headHeight, 0.1);
          const head = new THREE.Mesh(headGeometry, frameMaterial);
          head.position.set(0, 0.3 + headHeight / 2, -(bedLength + 0.2) / 2 + 0.05);
          head.castShadow = true;
          head.receiveShadow = true;
          bedGroup.add(head);
        }

        // Footboard for deluxe
        if (hasFootboard) {
          const footGeometry = new THREE.BoxGeometry(bedWidth + 0.2, 0.6, 0.1);
          const foot = new THREE.Mesh(footGeometry, frameMaterial);
          foot.position.set(0, 0.6, (bedLength + 0.2) / 2 - 0.05);
          foot.castShadow = true;
          foot.receiveShadow = true;
          bedGroup.add(foot);
        }

        // Pillows
        const numPillows = itemId === 'item2' ? 4 : 2; // Deluxe has more pillows
        const pillowWidth = bedWidth / (numPillows + 1);
        const pillowGeometry = new THREE.BoxGeometry(pillowWidth * 0.8, 0.2, 0.5, 4, 4, 4);
        for (let i = 0; i < numPillows; i++) {
          const pillow = new THREE.Mesh(pillowGeometry, beddingMaterial);
          const pillowY = mattressTopY + pillowGeometry.parameters.height / 2;
          const xPos = -bedWidth / 2 + pillowWidth + i * (bedWidth / numPillows);
          pillow.position.set(xPos, pillowY, -bedLength / 2 + 0.4);
          pillow.rotation.x = -Math.PI / 16;
          bedGroup.add(pillow);
        }
        bedGroup.rotation.y = -Math.PI / 2; // Rotate to place headboard against right wall

        bedGroup.position.set(...pos);
        bedGroup.userData = {
          category,
          originalScale: bedGroup.scale.clone(),
          originalColor: bedColor,
          itemId: itemId
        };
        interactiveObjectsRef.current.push(bedGroup as any);
        sceneRef.current?.add(bedGroup);

        // Store reference for updating
        if (category === 'põrandaliistud') {
          furnitureRef.current.bed.group = bedGroup;
        }
      };
      const createKitchenFurniture = (pos: [number, number, number], category: string, color: number, itemId: string = 'item1') => {
        const kitchenGroup = new THREE.Group();

        // Determine kitchen style based on itemId
        let kitchenWidth = 4;
        let kitchenColor = color;
        let counterColor = 0x2C2C2C;
        let hasUpperCabinets = true;
        let hasIsland = false;
        if (itemId === 'item1') {
          // Basic köögikomplekt
          kitchenWidth = 3.5;
          kitchenColor = 0xFFFFFF; // White cabinets
          counterColor = 0x696969; // Gray counter
        } else if (itemId === 'item2') {
          // Premium köögikomplekt
          kitchenWidth = 5;
          kitchenColor = 0x1C1C1C; // Black cabinets
          counterColor = 0xFFF8DC; // Cream marble counter
          hasIsland = true;
        }

        // --- Materials ---
        const cabinetMaterial = new THREE.MeshStandardMaterial({
          color: kitchenColor,
          roughness: 0.4,
          metalness: 0.2
        });
        const counterMaterial = new THREE.MeshStandardMaterial({
          color: counterColor,
          roughness: 0.3,
          metalness: 0.3
        });
        const handleMaterial = new THREE.MeshStandardMaterial({
          color: itemId === 'item2' ? 0xFFD700 : 0x888888,
          // Gold handles for premium
          metalness: 0.8,
          roughness: 0.2
        });
        const sinkMaterial = new THREE.MeshStandardMaterial({
          color: 0xcccccc,
          // Metallic sink
          metalness: 0.9,
          roughness: 0.2
        });

        // --- Lower Cabinets ---
        const cabinetWidth = kitchenWidth;
        const cabinetHeight = 0.9;
        const cabinetDepth = 0.6;
        const sinkWidth = 1.0; // Define sink width for creating a hole
        const sideCabinetWidth = (cabinetWidth - sinkWidth) / 2;

        // To avoid z-fighting, we create the cabinet and counter with a hole for the sink
        // Left cabinet part
        const cabinetSideGeom = new THREE.BoxGeometry(sideCabinetWidth, cabinetHeight, cabinetDepth);
        const cabinetLeft = new THREE.Mesh(cabinetSideGeom, cabinetMaterial);
        cabinetLeft.position.set(-(sinkWidth / 2 + sideCabinetWidth / 2), cabinetHeight / 2, 0);
        cabinetLeft.castShadow = true;
        cabinetLeft.receiveShadow = true;
        kitchenGroup.add(cabinetLeft);

        // Right cabinet part
        const cabinetRight = cabinetLeft.clone();
        cabinetRight.position.x = -cabinetLeft.position.x;
        kitchenGroup.add(cabinetRight);

        // Countertop
        const counterTopY = cabinetHeight + 0.025;
        const counterHeight = 0.05;
        const counterDepth = cabinetDepth + 0.1;
        const sideCounterWidth = (cabinetWidth + 0.05 - sinkWidth) / 2;
        const counterSideGeom = new THREE.BoxGeometry(sideCounterWidth, counterHeight, counterDepth);
        const counterLeft = new THREE.Mesh(counterSideGeom, counterMaterial);
        counterLeft.position.set(-(sinkWidth / 2 + sideCounterWidth / 2), counterTopY, 0);
        counterLeft.castShadow = true;
        counterLeft.receiveShadow = true;
        kitchenGroup.add(counterLeft);
        const counterRight = counterLeft.clone();
        counterRight.position.x = -counterLeft.position.x;
        kitchenGroup.add(counterRight);

        // Cabinet doors - create more doors for wider kitchens
        const doorWidth = 0.8;
        const numDoors = Math.floor(cabinetWidth / doorWidth);
        const doorGeometry = new THREE.BoxGeometry(doorWidth - 0.1, 0.8, 0.02);
        for (let i = 0; i < numDoors; i++) {
          const xPos = -cabinetWidth / 2 + doorWidth / 2 + i * doorWidth;
          // Skip door where sink is
          if (Math.abs(xPos) < sinkWidth / 2) continue;
          const door = new THREE.Mesh(doorGeometry, cabinetMaterial);
          door.position.set(xPos, 0.45, cabinetDepth / 2 + 0.011);
          door.castShadow = true;
          kitchenGroup.add(door);

          // Door handle
          const handleGeometry = new THREE.CylinderGeometry(0.02, 0.02, 0.1, 16);
          const handle = new THREE.Mesh(handleGeometry, handleMaterial);
          handle.position.set(xPos + doorWidth / 2 - 0.1, 0.5, cabinetDepth / 2 + 0.031);
          handle.rotation.z = Math.PI / 2;
          kitchenGroup.add(handle);
        }

        // --- Upper Cabinets ---
        if (hasUpperCabinets) {
          const upperCabinetHeight = itemId === 'item2' ? 1.0 : 0.8;
          const upperCabinetDepth = 0.35;
          const upperCabinetY = cabinetHeight + 0.6;
          const upperCabinetGeom = new THREE.BoxGeometry(cabinetWidth, upperCabinetHeight, upperCabinetDepth);
          const upperCabinet = new THREE.Mesh(upperCabinetGeom, cabinetMaterial);
          upperCabinet.position.set(0, upperCabinetY + upperCabinetHeight / 2, -(cabinetDepth - upperCabinetDepth) / 2);
          upperCabinet.castShadow = true;
          kitchenGroup.add(upperCabinet);

          // Glass doors for premium version
          if (itemId === 'item2') {
            const glassMaterial = new THREE.MeshStandardMaterial({
              color: 0x87CEEB,
              transparent: true,
              opacity: 0.3,
              roughness: 0.1,
              metalness: 0.1
            });
            const glassGeometry = new THREE.BoxGeometry(cabinetWidth * 0.9, upperCabinetHeight * 0.8, 0.02);
            const glass = new THREE.Mesh(glassGeometry, glassMaterial);
            glass.position.set(0, upperCabinetY + upperCabinetHeight / 2, -(cabinetDepth - upperCabinetDepth) / 2 + upperCabinetDepth / 2 + 0.02);
            kitchenGroup.add(glass);
          }
        }

        // --- Sink & Faucet ---
        const sinkGeometry = new THREE.BoxGeometry(1, 0.2, 0.5); // Made sink deeper
        const sink = new THREE.Mesh(sinkGeometry, sinkMaterial);
        // Position sink in the created hole, slightly below the counter
        sink.position.set(0, counterTopY - 0.1, 0.05);
        kitchenGroup.add(sink);
        const faucetBaseGeom = new THREE.CylinderGeometry(0.04, 0.04, 0.1, 16);
        const faucetBase = new THREE.Mesh(faucetBaseGeom, sinkMaterial);
        // Position faucet slightly above the counter to prevent z-fighting
        const faucetY = counterTopY + counterHeight / 2 + 0.001;
        faucetBase.position.set(0, faucetY, -0.2);
        kitchenGroup.add(faucetBase);
        const faucetSpoutGeom = new THREE.CylinderGeometry(0.02, 0.02, 0.2, 16);
        const faucetSpout = new THREE.Mesh(faucetSpoutGeom, sinkMaterial);
        faucetSpout.position.set(0, 0.1, -0.05); // Y is relative to faucet base
        faucetBase.add(faucetSpout);
        const faucetHandleGeom = new THREE.CylinderGeometry(0.01, 0.01, 0.15, 16);
        const faucetHandle = new THREE.Mesh(faucetHandleGeom, sinkMaterial);
        faucetHandle.position.set(0, 0.05, 0.05); // Y is relative to faucet base
        faucetHandle.rotation.x = Math.PI / 2;
        faucetBase.add(faucetHandle);

        // --- Kitchen Island for Premium ---
        if (hasIsland) {
          const islandGroup = new THREE.Group();
          const islandWidth = 2;
          const islandDepth = 1.2;

          // Island base
          const islandGeometry = new THREE.BoxGeometry(islandWidth, cabinetHeight, islandDepth);
          const island = new THREE.Mesh(islandGeometry, cabinetMaterial);
          island.position.set(0, cabinetHeight / 2, 2);
          island.castShadow = true;
          island.receiveShadow = true;
          islandGroup.add(island);

          // Island countertop
          const islandCounterGeometry = new THREE.BoxGeometry(islandWidth + 0.1, counterHeight, islandDepth + 0.1);
          const islandCounter = new THREE.Mesh(islandCounterGeometry, counterMaterial);
          islandCounter.position.set(0, cabinetHeight + counterHeight / 2, 2);
          islandCounter.castShadow = true;
          islandCounter.receiveShadow = true;
          islandGroup.add(islandCounter);

          // Bar stools
          const stoolMaterial = new THREE.MeshStandardMaterial({
            color: 0x2F4F4F,
            roughness: 0.8,
            metalness: 0.1
          });
          for (let i = -1; i <= 1; i++) {
            const stoolSeatGeometry = new THREE.CylinderGeometry(0.2, 0.2, 0.05, 16);
            const stoolSeat = new THREE.Mesh(stoolSeatGeometry, stoolMaterial);
            stoolSeat.position.set(i * 0.7, 1.1, 3);
            stoolSeat.castShadow = true;
            islandGroup.add(stoolSeat);

            // Stool legs
            const legGeometry = new THREE.CylinderGeometry(0.02, 0.02, 1, 8);
            for (let j = 0; j < 4; j++) {
              const angle = j * Math.PI / 2;
              const legX = Math.cos(angle) * 0.15;
              const legZ = Math.sin(angle) * 0.15;
              const leg = new THREE.Mesh(legGeometry, stoolMaterial);
              leg.position.set(i * 0.7 + legX, 0.55, 3 + legZ);
              leg.castShadow = true;
              islandGroup.add(leg);
            }
          }
          kitchenGroup.add(islandGroup);
        }
        kitchenGroup.position.set(...pos);
        kitchenGroup.rotation.y = Math.PI / 2; // Back against wall
        kitchenGroup.userData = {
          category,
          originalScale: kitchenGroup.scale.clone(),
          originalColor: kitchenColor,
          itemId: itemId
        };
        interactiveObjectsRef.current.push(kitchenGroup as any);
        sceneRef.current?.add(kitchenGroup);

        // Store reference for updating
        if (category === 'valisnurgad') {
          furnitureRef.current.kitchen.group = kitchenGroup;
        }
      };

      // Store functions for later use
      sceneFunctionsRef.current = {
        createSofa,
        createBed,
        createKitchenFurniture
      };

      // Create furniture objects repositioned within larger apartment
      // Create sofa based on selection
      const sofaItemId = selectedCategory === 'ukseliistud' && selectedItem ? selectedItem : 'item1';
      createSofa([-3.5, 0, -4.5], 'ukseliistud', 0x696969, sofaItemId);

      // Create kitchen based on selection
      const kitchenItemId = selectedCategory === 'valisnurgad' && selectedItem ? selectedItem : 'item1';
      createKitchenFurniture([-6, 0, 3], 'valisnurgad', 0xDEB887, kitchenItemId);

      // Create bed based on selection
      const bedItemId = selectedCategory === 'põrandaliistud' && selectedItem ? selectedItem : 'item1';
      createBed([5.5, 0, 0], 'põrandaliistud', 0xF0F8FF, bedItemId);

      // Movement Marker - Make it more visible
      const markerGroup = new THREE.Group();

      // Outer ring
      const markerGeometry = new THREE.RingGeometry(0.3, 0.35, 32);
      const markerMaterial = new THREE.MeshBasicMaterial({
        color: 0x4a90e2,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.8
      });
      const markerRing = new THREE.Mesh(markerGeometry, markerMaterial);
      markerRing.rotation.x = -Math.PI / 2;
      markerGroup.add(markerRing);

      // Inner circle
      const innerMarkerGeometry = new THREE.CircleGeometry(0.1, 32);
      const innerMarkerMaterial = new THREE.MeshBasicMaterial({
        color: 0x4a90e2,
        transparent: true,
        opacity: 1,
        side: THREE.DoubleSide
      });
      const innerMarker = new THREE.Mesh(innerMarkerGeometry, innerMarkerMaterial);
      innerMarker.rotation.x = -Math.PI / 2;
      innerMarker.position.y = 0.001;
      markerGroup.add(innerMarker);
      markerGroup.position.y = 0.01;
      markerGroup.visible = false;

      // Mark the marker group and all its children to be ignored by raycaster
      markerGroup.userData.isMarker = true;
      markerGroup.traverse(child => {
        child.userData.isMarker = true;
      });
      moveMarkerRef.current = markerGroup as any;
      sceneRef.current.add(markerGroup);
    };
    const updateCameraRotation = () => {
      if (!cameraRef.current) return;

      // Use quaternion-based rotation for more stable camera behavior
      // THREE.Euler expects (x, y, z) where x=pitch, y=yaw, z=roll
      const euler = new THREE.Euler(cameraRotationRef.current.pitch, cameraRotationRef.current.yaw, 0, 'YXZ');

      // Set camera rotation using quaternion to avoid gimbal lock
      cameraRef.current.setRotationFromEuler(euler);

      // Ensure the up vector stays exactly vertical
      cameraRef.current.up.set(0, 1, 0);
    };
    const handleResize = () => {
      if (!cameraRef.current || !rendererRef.current) return;
      cameraRef.current.aspect = window.innerWidth / window.innerHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(window.innerWidth, window.innerHeight);
    };
    const handleMouseDown = (event: MouseEvent) => {
      // Prevent text selection while dragging
      event.preventDefault();
      isDraggingRef.current = true;
      mouseMovedSinceDown.current = false;
      previousMousePosition.current = {
        x: event.clientX,
        y: event.clientY
      };
    };
    const handleMouseMove = (event: MouseEvent) => {
      // Always update mouse position for hover effects
      mouseRef.current.x = event.clientX / window.innerWidth * 2 - 1;
      mouseRef.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
      if (!isDraggingRef.current) return;

      // Mark that mouse has moved (to distinguish from click)
      const deltaX = event.clientX - previousMousePosition.current.x;
      const deltaY = event.clientY - previousMousePosition.current.y;
      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
        mouseMovedSinceDown.current = true;
      }

      // Update camera rotation with Matterport-like constraints
      cameraRotationRef.current.yaw += deltaX * 0.003;
      cameraRotationRef.current.pitch += deltaY * 0.003; // Fixed: reversed the direction for natural feel

      // Much stricter pitch constraints to prevent any upside down rotation (like Matterport)
      // Limit vertical rotation to approximately -45° to +45°
      const maxPitch = Math.PI / 4; // 45 degrees
      const minPitch = -Math.PI / 4; // -45 degrees
      cameraRotationRef.current.pitch = Math.max(minPitch, Math.min(maxPitch, cameraRotationRef.current.pitch));

      // Normalize yaw to prevent accumulation issues
      while (cameraRotationRef.current.yaw > Math.PI) {
        cameraRotationRef.current.yaw -= Math.PI * 2;
      }
      while (cameraRotationRef.current.yaw < -Math.PI) {
        cameraRotationRef.current.yaw += Math.PI * 2;
      }
      updateCameraRotation();
      previousMousePosition.current = {
        x: event.clientX,
        y: event.clientY
      };
    };
    const handleMouseUp = () => {
      isDraggingRef.current = false;
    };
    const handleClick = (event: MouseEvent) => {
      if (mouseMovedSinceDown.current || !cameraRef.current || !sceneRef.current) return;

      // Calculate mouse position
      const mouse = new THREE.Vector2();
      mouse.x = event.clientX / window.innerWidth * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      raycaster.setFromCamera(mouse, cameraRef.current);
      const allIntersects = raycaster.intersectObjects(sceneRef.current.children, true);

      // Filter out markers and other non-clickable objects
      const intersects = allIntersects.filter(intersect => !intersect.object.userData.isMarker);
      if (intersects.length > 0) {
        const firstIntersect = intersects[0];

        // Find the top-level group that has category data (same logic as hover)
        let clickedGroup: THREE.Group | null = null;
        let current: THREE.Object3D | null = firstIntersect.object;
        while (current && !current.userData.category) {
          current = current.parent;
        }
        if (current && current.userData.category) {
          clickedGroup = current as THREE.Group;
        }

        // Check if clicked on interactive object
        if (clickedGroup && clickedGroup.userData.category) {
          onFocusChangeRef.current(clickedGroup.userData.category, true);
          return;
        }

        // Otherwise, move to the clicked location (ONLY on floor, not walls)
        if (firstIntersect.object.userData.isFloor) {
          const targetPoint = firstIntersect.point.clone();
          targetPositionRef.current = targetPoint.clone();
          targetPositionRef.current.y = 1.6; // Eye level

          // Don't change rotation when moving - just move camera position
          // This allows user to keep their current viewing direction
          targetRotationRef.current = null;
          movingRef.current = true;

          // Call navigation callback when user starts moving
          onNavigationRef.current?.();
        }
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      // Prevent default behavior for arrow keys to avoid page scrolling
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
      }
      switch (event.key.toLowerCase()) {
        case 'w':
          keysPressed.current.w = true;
          break;
        case 'a':
          keysPressed.current.a = true;
          break;
        case 's':
          keysPressed.current.s = true;
          break;
        case 'd':
          keysPressed.current.d = true;
          break;
        case 'arrowup':
          keysPressed.current.arrowUp = true;
          break;
        case 'arrowdown':
          keysPressed.current.arrowDown = true;
          break;
        case 'arrowleft':
          keysPressed.current.arrowLeft = true;
          break;
        case 'arrowright':
          keysPressed.current.arrowRight = true;
          break;
      }

      // Call navigation callback when starting keyboard movement
      const anyKeyPressed = Object.values(keysPressed.current).some(v => v);
      if (anyKeyPressed) {
        onNavigationRef.current?.();
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      switch (event.key.toLowerCase()) {
        case 'w':
          keysPressed.current.w = false;
          break;
        case 'a':
          keysPressed.current.a = false;
          break;
        case 's':
          keysPressed.current.s = false;
          break;
        case 'd':
          keysPressed.current.d = false;
          break;
        case 'arrowup':
          keysPressed.current.arrowUp = false;
          break;
        case 'arrowdown':
          keysPressed.current.arrowDown = false;
          break;
        case 'arrowleft':
          keysPressed.current.arrowLeft = false;
          break;
        case 'arrowright':
          keysPressed.current.arrowRight = false;
          break;
      }
    };
    const animate = () => {
      frameIdRef.current = requestAnimationFrame(animate);
      if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return;

      // Center-screen raycasting for automatic object detection (always active)
      let centerFocusedGroup: THREE.Group | null = null;

      // Cast multiple rays in an elliptical pattern around the center for larger targeting radius
      const numRays = 12; // Number of rays in elliptical pattern (increased for better coverage)
      const horizontalRadius = 0.11; // Horizontal radius in normalized screen coordinates
      const verticalRadius = 0.22; // Vertical radius - 2x larger than horizontal

      // First check exact center
      const centerMouse = new THREE.Vector2(0, 0);
      raycaster.setFromCamera(centerMouse, cameraRef.current);
      const centerIntersects = raycaster.intersectObjects(interactiveObjectsRef.current, true);
      if (centerIntersects.length > 0) {
        let current: THREE.Object3D | null = centerIntersects[0].object;
        while (current && !current.userData.category) {
          current = current.parent;
        }
        if (current && current.userData.category) {
          centerFocusedGroup = current as THREE.Group;
        }
      }

      // If no direct hit, check in an elliptical pattern
      if (!centerFocusedGroup) {
        for (let i = 0; i < numRays; i++) {
          const angle = i / numRays * Math.PI * 2;
          const offsetX = Math.cos(angle) * horizontalRadius;
          const offsetY = Math.sin(angle) * verticalRadius;
          const offsetMouse = new THREE.Vector2(offsetX, offsetY);
          raycaster.setFromCamera(offsetMouse, cameraRef.current);
          const offsetIntersects = raycaster.intersectObjects(interactiveObjectsRef.current, true);
          if (offsetIntersects.length > 0) {
            let current: THREE.Object3D | null = offsetIntersects[0].object;
            while (current && !current.userData.category) {
              current = current.parent;
            }
            if (current && current.userData.category) {
              centerFocusedGroup = current as THREE.Group;
              break; // Found an object, stop checking
            }
          }
        }
      }

      // Handle object highlighting based on mouse position (only when not dragging for mouse hover)
      let hoveredGroup: THREE.Group | null = null;
      if (!isDraggingRef.current) {
        const mouse = new THREE.Vector2(mouseRef.current.x, mouseRef.current.y);
        raycaster.setFromCamera(mouse, cameraRef.current);
        const intersects = raycaster.intersectObjects(interactiveObjectsRef.current, true);

        // Find the top-level group that has category data
        if (intersects.length > 0) {
          let current: THREE.Object3D | null = intersects[0].object;
          while (current && !current.userData.category) {
            current = current.parent;
          }
          if (current && current.userData.category) {
            hoveredGroup = current as THREE.Group;
          }
        }

        // Show move marker on floor - always track mouse position
        const allHoverIntersects = raycaster.intersectObjects(sceneRef.current.children, true);
        const hoverIntersects = allHoverIntersects.filter(intersect => !intersect.object.userData.isMarker);
        if (moveMarkerRef.current) {
          const floorIntersect = hoverIntersects.find(intersect => intersect.object.userData.isFloor);
          // Always show marker on floor unless we're actively moving to a location
          if (floorIntersect && !movingRef.current) {
            moveMarkerRef.current.position.copy(floorIntersect.point);
            moveMarkerRef.current.position.y = 0.01;
            moveMarkerRef.current.visible = true;

            // Change cursor based on what we're hovering over
            if (mountRef.current) {
              if (hoveredGroup || centerFocusedGroup) {
                mountRef.current.style.cursor = 'pointer';
              } else {
                mountRef.current.style.cursor = 'pointer'; // Always pointer when marker is visible
              }
            }
          } else if (!floorIntersect) {
            // Only hide if we're not over the floor at all
            moveMarkerRef.current.visible = false;
            if (mountRef.current) {
              if (hoveredGroup || centerFocusedGroup) {
                mountRef.current.style.cursor = 'pointer';
              } else {
                mountRef.current.style.cursor = 'grab';
              }
            }
          }
        }
      } else {
        // Keep marker visible even while dragging/rotating
        const mouse = new THREE.Vector2(mouseRef.current.x, mouseRef.current.y);
        raycaster.setFromCamera(mouse, cameraRef.current);
        const dragIntersects = raycaster.intersectObjects(sceneRef.current.children, true);
        const dragHoverIntersects = dragIntersects.filter(intersect => !intersect.object.userData.isMarker);
        if (moveMarkerRef.current) {
          const floorIntersect = dragHoverIntersects.find(intersect => intersect.object.userData.isFloor);
          if (floorIntersect && !movingRef.current) {
            moveMarkerRef.current.position.copy(floorIntersect.point);
            moveMarkerRef.current.position.y = 0.01;
            moveMarkerRef.current.visible = true;
          } else {
            moveMarkerRef.current.visible = false;
          }
        }
        if (mountRef.current) {
          mountRef.current.style.cursor = 'grabbing';
        }
      }

      // Check if any category is selected - if so, disable all highlights
      const isCategoryOpen = selectedCategoryRef.current !== '';

      // Clear all highlights first
      if (lastFocusedObject) {
        lastFocusedObject.traverse(child => {
          if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
            const material = child.material;
            material.emissive = new THREE.Color(0x000000);
            material.emissiveIntensity = 0;
            material.transparent = false;
            material.opacity = 1;
          }
        });
      }

      // Priority system: Mouse hover > Center focus
      let activeGroup: THREE.Group | null = null;
      let shouldHighlight = false;
      let isMouseHover = false;

      // Determine active group first
      if (hoveredGroup && hoveredGroup.userData.category) {
        // Mouse hover has highest priority
        activeGroup = hoveredGroup;
        isMouseHover = true;
        // Only show highlight if category is not open
        if (!isCategoryOpen) {
          shouldHighlight = true;
        }
      } else if (centerFocusedGroup && centerFocusedGroup.userData.category) {
        // Center focus as fallback
        activeGroup = centerFocusedGroup;
        isMouseHover = false;
        // Pulse timing will determine if should highlight
      }

      // Handle pulse timing (only for center focus, not mouse hover, and not when any category is open)
      const currentTime = Date.now();
      if (activeGroup && !isMouseHover && !isCategoryOpen) {
        // Start or continue pulse timer
        if (!pulseTimerRef.current) {
          pulseTimerRef.current = currentTime;
          isPulsingRef.current = true;
          lastPulseTimeRef.current = currentTime;
        }

        // Check if it's time to pulse (every 10 seconds)
        if (currentTime - lastPulseTimeRef.current >= 5000) {
          isPulsingRef.current = true;
          lastPulseTimeRef.current = currentTime;
        }

        // Handle pulse animation (0.5 second fade in, 0.5 second hold, 0.5 second fade out)
        if (isPulsingRef.current) {
          const pulseElapsed = currentTime - lastPulseTimeRef.current;
          if (pulseElapsed < 500) {
            // Fade in
            pulsePhaseRef.current = pulseElapsed / 500;
            shouldHighlight = true;
          } else if (pulseElapsed < 1000) {
            // Hold
            pulsePhaseRef.current = 1;
            shouldHighlight = true;
          } else if (pulseElapsed < 1500) {
            // Fade out
            pulsePhaseRef.current = 1 - (pulseElapsed - 1000) / 500;
            shouldHighlight = true;
          } else {
            // Pulse complete
            isPulsingRef.current = false;
            pulsePhaseRef.current = 0;
            shouldHighlight = false;
          }
        }
      } else if (!activeGroup) {
        // Reset pulse timer when no object is focused
        pulseTimerRef.current = 0;
        isPulsingRef.current = false;
        pulsePhaseRef.current = 0;
      }

      // If any category is open, disable all highlights
      if (isCategoryOpen) {
        shouldHighlight = false;
      }

      // Apply highlight to active group
      if (activeGroup && shouldHighlight) {
        let intensity = 0.15;
        let opacity = 0.9;
        if (isMouseHover) {
          // Full intensity for mouse hover
          intensity = 0.15;
          opacity = 0.9;
        } else {
          // Pulsing intensity for center focus
          intensity = 0.15 * pulsePhaseRef.current;
          opacity = 0.9 + 0.1 * pulsePhaseRef.current;
        }
        activeGroup.traverse(child => {
          if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
            const material = child.material;
            material.emissive = new THREE.Color(0x4169E1); // Royal blue
            material.emissiveIntensity = intensity;
            material.transparent = true;
            material.opacity = opacity;
          }
        });
        lastFocusedObject = activeGroup;

        // Call onFocusChange
        if (lastHoveredCategory !== activeGroup.userData.category || isPulsingRef.current || isMouseHover) {
          lastHoveredCategory = activeGroup.userData.category;
          onFocusChangeRef.current(activeGroup.userData.category, false);
        }
      } else {
        lastFocusedObject = null;
        if (lastHoveredCategory !== '') {
          lastHoveredCategory = '';
          onFocusChangeRef.current('', false);
        }
      }

      // Handle smooth camera movement
      if (movingRef.current && targetPositionRef.current) {
        const camera = cameraRef.current;
        const distance = camera.position.distanceTo(targetPositionRef.current);
        if (distance < 0.1) {
          movingRef.current = false;
          targetRotationRef.current = null;
        } else {
          // Move camera position
          camera.position.lerp(targetPositionRef.current, 0.08);

          // Smoothly rotate camera if we have a target rotation
          if (targetRotationRef.current) {
            // Interpolate rotation
            const rotationSpeed = 0.1;
            cameraRotationRef.current.yaw += (targetRotationRef.current.yaw - cameraRotationRef.current.yaw) * rotationSpeed;
            cameraRotationRef.current.pitch += (targetRotationRef.current.pitch - cameraRotationRef.current.pitch) * rotationSpeed;
          }
        }
      }

      // Always update camera rotation (allows looking around while moving)
      updateCameraRotation();

      // Handle keyboard navigation
      const keys = keysPressed.current;
      const anyMovementKey = keys.w || keys.a || keys.s || keys.d || keys.arrowUp || keys.arrowDown || keys.arrowLeft || keys.arrowRight;
      if (cameraRef.current && anyMovementKey) {
        // Movement speed
        const moveSpeed = 0.1;

        // Handle movement with WASD and arrow keys
        const camera = cameraRef.current;
        const moveVector = new THREE.Vector3();

        // Get forward/backward direction based on camera yaw (ignore pitch for movement)
        const yaw = cameraRotationRef.current.yaw;
        const forwardDirection = new THREE.Vector3(-Math.sin(yaw), 0, -Math.cos(yaw));

        // Get right direction (perpendicular to forward) - fixed calculation
        const rightDirection = new THREE.Vector3(-forwardDirection.z, 0, forwardDirection.x);

        // Accumulate movement based on pressed keys (WASD or arrows)
        if (keys.w || keys.arrowUp) {
          moveVector.add(forwardDirection);
        }
        if (keys.s || keys.arrowDown) {
          moveVector.sub(forwardDirection);
        }
        if (keys.d || keys.arrowRight) {
          moveVector.add(rightDirection);
        }
        if (keys.a || keys.arrowLeft) {
          moveVector.sub(rightDirection);
        }

        // Normalize diagonal movement
        if (moveVector.length() > 0) {
          moveVector.normalize();
          moveVector.multiplyScalar(moveSpeed);

          // Calculate new position
          const newPosition = camera.position.clone().add(moveVector);

          // Room boundaries check
          const roomHalfWidth = 7 - 0.5; // Room is 14 wide, minus margin
          const roomHalfDepth = 5 - 0.5; // Room is 10 deep, minus margin

          // Clamp position to room boundaries
          newPosition.x = Math.max(-roomHalfWidth, Math.min(roomHalfWidth, newPosition.x));
          newPosition.z = Math.max(-roomHalfDepth, Math.min(roomHalfDepth, newPosition.z));

          // Apply the new position
          camera.position.copy(newPosition);

          // Clear any click-based movement when using keyboard
          movingRef.current = false;
          targetPositionRef.current = null;
        }
      }
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    };
    init();
    animate();
    mountRef.current?.addEventListener('mousedown', handleMouseDown);
    mountRef.current?.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    mountRef.current?.addEventListener('click', handleClick);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('resize', handleResize);
    return () => {
      if (frameIdRef.current) cancelAnimationFrame(frameIdRef.current);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mouseup', handleMouseUp);
      mountRef.current?.removeEventListener('mousedown', handleMouseDown);
      mountRef.current?.removeEventListener('mousemove', handleMouseMove);
      mountRef.current?.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      rendererRef.current?.dispose();
      if (mountRef.current && rendererRef.current?.domElement) {
        mountRef.current.removeChild(rendererRef.current.domElement);
      }
    };
  }, []);

  // Handle camera animation when category is selected
  useEffect(() => {
    if (!selectedCategory || !cameraRef.current) return;
    const preset = cameraPresetsRef.current[selectedCategory];
    if (preset) {
      // Set target position and rotation for smooth animation
      targetPositionRef.current = preset.position.clone();
      targetRotationRef.current = {
        yaw: preset.rotation.yaw,
        pitch: preset.rotation.pitch
      };
      movingRef.current = true;
    }
  }, [selectedCategory]);

  // Update furniture when selection changes
  useEffect(() => {
    if (!sceneRef.current || !selectedCategory || !selectedItem) return;
    if (!sceneFunctionsRef.current.createSofa || !sceneFunctionsRef.current.createBed || !sceneFunctionsRef.current.createKitchenFurniture) return;

    // Find and update the appropriate furniture based on category
    if (selectedCategory === 'ukseliistud' && furnitureRef.current.sofa.group) {
      // Remove old sofa
      sceneRef.current.remove(furnitureRef.current.sofa.group);
      // Remove from interactive objects array
      const index = interactiveObjectsRef.current.indexOf(furnitureRef.current.sofa.group);
      if (index > -1) {
        interactiveObjectsRef.current.splice(index, 1);
      }
      furnitureRef.current.sofa.group = null;

      // Create new sofa
      sceneFunctionsRef.current.createSofa(furnitureRef.current.sofa.position, 'ukseliistud', 0x696969, selectedItem);
    } else if (selectedCategory === 'põrandaliistud' && furnitureRef.current.bed.group) {
      // Remove old bed
      sceneRef.current.remove(furnitureRef.current.bed.group);
      // Remove from interactive objects array
      const index = interactiveObjectsRef.current.indexOf(furnitureRef.current.bed.group);
      if (index > -1) {
        interactiveObjectsRef.current.splice(index, 1);
      }
      furnitureRef.current.bed.group = null;

      // Create new bed
      sceneFunctionsRef.current.createBed(furnitureRef.current.bed.position, 'põrandaliistud', 0xF0F8FF, selectedItem);
    } else if (selectedCategory === 'valisnurgad' && furnitureRef.current.kitchen.group) {
      // Remove old kitchen
      sceneRef.current.remove(furnitureRef.current.kitchen.group);
      // Remove from interactive objects array
      const index = interactiveObjectsRef.current.indexOf(furnitureRef.current.kitchen.group);
      if (index > -1) {
        interactiveObjectsRef.current.splice(index, 1);
      }
      furnitureRef.current.kitchen.group = null;

      // Create new kitchen
      sceneFunctionsRef.current.createKitchenFurniture(furnitureRef.current.kitchen.position, 'valisnurgad', 0xDEB887, selectedItem);
    }
  }, [selectedCategory, selectedItem]);
  return <div className="absolute inset-0 w-full h-full" data-id="element-155">
      <div ref={mountRef} className="absolute inset-0 w-full h-full" style={{
      cursor: 'grab'
    }} data-id="element-156" />
    </div>;
};